import clsx from 'clsx'
import { Button, Dropdown, HelperText, Icon, Popover } from 'gu-ui-kit'
import React, { useMemo } from 'react'
import RadioGroup from 'components/FormControls/subcomponents/RadioGroup'
import CheckboxGroup from 'components/FormControls/subcomponents/CheckboxGroup'
import styles from 'components/FormControls/form-controls.module.scss'

const selectorRender = ({
  input: { value, name, onChange },
  meta: { touched, error, submitting },
  size = 'm',
  startIcon = null,
  className = '',
  renderOption = ({ label }) => label,
  dropdownClassName = '',
  helperText = null,
  multiple = false,
  renderSelectedValue = ({ value, items, multiple }) =>
    multiple
      ? items
          ?.filter((item) => value?.includes(item.value))
          ?.map(({ label }) => label)
          ?.join(', ') ?? null
      : items?.find((item) => item.value === value)?.label ?? null,
  renderDropdown = ({
    name,
    value,
    items,
    onClose,
    onChange,
    showActionButtons,
    renderLabel,
    Component,
    onDrop,
  }) => (
    <Component
      name={name}
      value={value}
      options={items}
      renderLabel={renderLabel}
      optionsWrapperClassName={styles['selector__options-wrapper']}
      onApply={(value) => {
        onClose()
        onChange(value)
      }}
      onDrop={onDrop}
      showActionButtons={showActionButtons}
    />
  ),
  disabled = false,
  fullWidth = false,
  label = '',
  items = [],
  t = () => {},
}) => {
  const { bindTrigger, bindPopover, usePopupState } = Popover.usePopover()

  const popoverState = usePopupState({
    variant: 'popover',
    id: 'selectorPopover-' + name,
  })

  const bindProps = bindTrigger(popoverState)

  const isSelectedValue = useMemo(() => value?.length > 0, [value])

  if (submitting || disabled) {
    bindProps.onClick = (e) => e.preventDefault()
  }

  return (
    <div className={styles['selector']}>
      <Button
        {...bindProps}
        className={clsx(styles['selector__button'], styles[`selector__button_${size}`], className, {
          [styles[`selector__button_with-value`]]: isSelectedValue,
        })}
        size={size}
        fullWidth={fullWidth}
        startIcon={startIcon}
        endIcon={
          <Icon
            color={'Interface-Seventh'}
            size={'m'}
            name={popoverState.isOpen ? 'arrow-up' : 'arrow-down'}
          />
        }
        disabled={submitting || disabled}
        variant={'primary'}
        color={'Label-Secondary'}
        colorHover={'Label-Secondary'}
        colorActive={'Label-Secondary'}
        colorDisabled={'Label-Quaternary'}
        bgColor={'Background-Secondary'}
        bgColorHover={'Background-Secondary'}
        bgColorActive={'Background-Secondary'}
        bgColorDisabled={'Background-Quaternary'}
        borderColor={'Background-Secondary'}
        borderColorHover={'Interface-Fifth'}
        borderColorActive={'Interface-Fifth'}
        borderColorDisabled={'Background-Quaternary'}
      >
        <div className={clsx(styles['selector__button-inner'])}>
          <div
            className={clsx(styles['selector__button-label'], {
              [styles['selector__button-label_with-value']]: isSelectedValue,
            })}
          >
            {label}
          </div>
          {isSelectedValue && (
            <div className={clsx(styles['selector__button-value'])}>
              {renderSelectedValue({
                value,
                items,
                multiple,
              })}
            </div>
          )}
        </div>
      </Button>
      {touched && error ? (
        <HelperText error>{t(error)}</HelperText>
      ) : helperText ? (
        <HelperText>{helperText}</HelperText>
      ) : null}

      <Popover
        {...bindPopover(popoverState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 8,
          horizontal: 'left',
        }}
      >
        <Dropdown className={clsx(styles['selector__dropdown'], dropdownClassName)}>
          {renderDropdown({
            name,
            value,
            items,
            onClose: popoverState.close,
            onChange,
            showActionButtons: true,
            renderLabel: renderOption,
            Component: multiple ? CheckboxGroup : RadioGroup,
            onDrop: () => {
              onChange(multiple ? [] : '')
            },
          })}
        </Dropdown>
      </Popover>
    </div>
  )
}

export default selectorRender
